import { type ReactNode } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Avatar } from '@/components/ui/avatar';
import { CategoryBadge } from '@/components/ui/category-badge';
import type { Creator } from '@/types';
import { formatFollowers } from '@/utils/formatters';
import { creatorPhotoUrl } from '@/utils/creator-helpers';
import { cn } from '@/utils/cn';

type Props = { creator: Creator; onClick?: () => void };

function CardContent({ creator }: { creator: Creator }): ReactNode {
  return (
    <div className="flex items-center gap-sm">
      <Avatar
        src={creatorPhotoUrl(creator)}
        name={creator.ig_username}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-text-primary">
          @{creator.ig_username}
        </p>
        <div className="mt-0.5 flex items-center gap-xs">
          <span className="text-[10px] tabular-nums text-text-tertiary">
            {formatFollowers(creator.followers)}
          </span>
          <CategoryBadge
            type={creator.creator_type}
            category={creator.category}
          />
        </div>
      </div>
    </div>
  );
}

export function KanbanCard({ creator, onClick }: Props): ReactNode {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: creator.id,
    data: { status: creator.status, creator },
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onClick}
      className={cn(
        'w-full cursor-grab rounded-md border border-white/[0.04] bg-surface-elevated p-sm text-left transition-colors hover:border-white/[0.08] active:cursor-grabbing',
        isDragging && 'cursor-grabbing opacity-30'
      )}
      {...listeners}
      {...attributes}
    >
      <CardContent creator={creator} />
    </button>
  );
}
